// --------------------------
// Imports
// --------------------------
import { App, LumenRevision } from "manager/hooks/application-summary/mappers/interfaces";
import { normalizeOutcomesToArray } from "shared/components/vega/LumenV2/utils";
import { isPersonaIncomplete } from "shared/utils/persona";

import {
  CategoryId,
  EXPIRED_BACKGROUND_REPORTS_MESSAGE,
} from "../../constants";

import {
  queryByAnnotation,
  isBackgroundReportIncomplete,
  getIncomeAndCashRunwayData,
} from "../../queryHelpers";

import {
  getBaseCategoryLabel,
  getOutcomeBasedLabel,
  getNumericLabel,
  appendSentilinkToLabel,
} from "./labelHelpers";
import { getDealGroupIncomeLines } from "../../helpers/incomeDisplayHelpers";
import {
  determineAggregateStatus,
  getPendingStatus,
  createDefaultCategoryData,
  getCategoryStatusFromOutcomes,
  getIncompleteBackgroundDetails,
} from "./outcomeHelpers";

export interface CategoryResult {
  status: string;
  cosignApproved?: boolean;
  label: string;
  details?: string[];
  tooltipContent?: string;
}

export interface ProcessIdVerificationParams {
  isLumenStillCalculating: boolean;
  lumenRun: any; // LumenRun type varies across the codebase
  applications?: App[];
}

/**
 * True when any triggered, not-manually-verified applicant is in the Needs
 * Review fraud-risk band. Matches applicant-card eligibility (triggered +
 * NEEDS_REVIEW, excluding BLOCK / ERROR / manual verify via
 * TIER_CARD_STATUS / getIdCardChromeStatus) and getSentilinkLabel's
 * PRODUCT-2164 active filter; used to escalate header chrome only (PRODUCT-2429).
 */
function hasActiveNeedsReviewFraudRisk(applications?: App[]): boolean {
  if (!applications?.length) {
    return false;
  }

  return applications.some((app) => {
    const sentilink = app?.sentilink;
    if (!sentilink?.triggered || sentilink.identityManuallyVerified) {
      return false;
    }
    // BLOCK/ERROR are not the Needs Review band on the applicant card; do not
    // soften them to header warn if a stale NEEDS_REVIEW tier is still present.
    if (sentilink.outcome === "BLOCK" || sentilink.outcome === "ERROR") {
      return false;
    }
    return sentilink.riskTier === "NEEDS_REVIEW";
  });
}

/**
 * Background tiles show no Lumen data derived from expired reports: one expired
 * applicant switches the deal-level credit, rental and criminal tiles.
 */
function getExpiredBackgroundReportsResult(
  applications?: App[]
): CategoryResult | null {
  if (!applications?.some((app) => app?.hasExpiredBackgroundReports)) {
    return null;
  }

  return { status: "pending", label: EXPIRED_BACKGROUND_REPORTS_MESSAGE };
}

// --------------------------
// Category Processors
// --------------------------

/**
 * Process ID verification data for each applicant.
 *
 * When personaStatus is undefined (legacy applications), falls through to
 * Lumen outcomes. This is intentional - we only show "pending" when we
 * explicitly know Persona verification is incomplete.
 *
 * Chrome status is Lumen-driven, except PRODUCT-2429: an active Needs Review
 * fraud-risk tier escalates pass/warn chrome to `warn` so the header matches
 * the applicant card. Lumen fail still wins. HIGH_RISK / Error stay
 * informational on this card (out of scope for PRODUCT-2429).
 */
export function processIdVerification({
  isLumenStillCalculating,
  lumenRun,
  applications,
}: ProcessIdVerificationParams): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  // Persona "incomplete" should not override a known completed verification.
  const hasPendingPersonaVerification = applications?.some(
    (app) =>
      isPersonaIncomplete(app?.personaStatus) &&
      app?.applicant?.identity?.verified !== true
  );
  if (hasPendingPersonaVerification) {
    return getPendingStatus();
  }

  const idVerificationData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["identity-verification"]
  );

  // Set details from outcomes
  idVerificationData.details = details;

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    idVerificationData.status = outcomeStatus;
  }

  // Build the first label line from the pre-escalation Lumen status. Escalating
  // chrome to warn must not rewrite "ID Accepted" to "ID Verification Issues"
  // (PRODUCT-2429 AC: first line stays pass wording when identity passed).
  const lumenStatusForLabel = idVerificationData.status;
  idVerificationData.label = appendSentilinkToLabel(
    getBaseCategoryLabel("id", lumenStatusForLabel),
    applications
  );

  // PRODUCT-2429: Needs Review is not a Lumen identity fail, but managers need
  // warn chrome at a glance. Only escalate pass/warn — never Lumen fail, and
  // never pending (e.g. missing lumenRun). Label was already built from
  // lumenStatusForLabel above so pass wording is preserved.
  if (
    (idVerificationData.status === "pass" ||
      idVerificationData.status === "warn") &&
    hasActiveNeedsReviewFraudRisk(applications)
  ) {
    idVerificationData.status = "warn";
  }

  return idVerificationData;
}

/**
 * Extract the threshold value from a lumen formula entry's evaluatedFormula or formula string.
 * evaluatedFormula e.g. "((3.443) >= 3.1)" → last number → 3.1
 * formula e.g. "#income-to-rent-ideal:((value) >= 3.1)" → last number → 3.1
 */
function extractThresholdFromEntry(entry: any): number | null {
  if (!entry || typeof entry !== 'object') return null;

  // Prefer evaluatedFormula (has actual values substituted), fall back to raw formula
  const formulaStr = entry.evaluatedFormula || entry.formula || '';
  const matches = formulaStr.match(/[\d.]+/g);
  if (matches && matches.length >= 1) {
    const val = parseFloat(matches[matches.length - 1]);
    if (!isNaN(val)) return val;
  }

  return null;
}

/**
 * Build tooltip text for income-to-rent (and optional cash runway) requirement.
 * Prefers threshold values extracted from the lumen formula evaluation history;
 * falls back to lumenRevision config values.
 */
function buildIncomeToRentTooltip(applicationLumenResult: any, lumenRevision?: LumenRevision): string | undefined {
  const parts: string[] = [];

  // Pass null (not undefined) as key so JS doesn't trigger the default "result" parameter,
  // returning the full history entry object instead of just entry.result.
  const incomeIdealEntry = queryByAnnotation(applicationLumenResult, 'income-to-rent-ideal', null);
  const incomeThresholdFromFormula = extractThresholdFromEntry(incomeIdealEntry);
  const incomeThreshold = incomeThresholdFromFormula ?? (lumenRevision?.useIncomeRentRatio ? lumenRevision?.idealIncomeRentRatio : null);

  if (incomeThreshold) {
    parts.push(`Ideal Income/Rent: ${incomeThreshold}x`);
  }

  const cashIdealEntry = queryByAnnotation(applicationLumenResult, 'income-cash-runway-ideal', null);
  const cashThresholdFromFormula = extractThresholdFromEntry(cashIdealEntry);
  const cashThreshold = cashThresholdFromFormula ?? (lumenRevision?.useAssets ? lumenRevision?.idealAssetsRatio : null);

  if (cashThreshold) {
    parts.push(`Ideal Cash Runway: ${cashThreshold}x`);
  }

  return parts.length > 0 ? parts.join('\n') : undefined;
}

function getIdealIncomeRentRatio(
  applicationLumenResult: any,
  lumenRevision?: LumenRevision
): number | null {
  const incomeIdealEntry = queryByAnnotation(
    applicationLumenResult,
    'income-to-rent-ideal',
    null
  );
  const incomeThresholdFromFormula = extractThresholdFromEntry(incomeIdealEntry);
  const incomeThreshold =
    incomeThresholdFromFormula ??
    (lumenRevision?.useIncomeRentRatio
      ? lumenRevision?.idealIncomeRentRatio
      : null);

  return incomeThreshold ?? null;
}

/**
 * Process income data from application result
 */
export function processIncome({
  applicationLumenResult,
  isLumenStillCalculating,
  lumenRun,
  lumenRevision = undefined,
  enableMonthlyIncomeDisplay = false,
  monthlyRentDollars = undefined,
}: {
  applicationLumenResult: any;
  isLumenStillCalculating: boolean;
  lumenRun: any;
  lumenRevision?: LumenRevision;
  enableMonthlyIncomeDisplay?: boolean;
  monthlyRentDollars?: number | null;
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  const incomeData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["income"]
  );

  const {
    incomeToRentRatio,
    cashRunway,
    status: statusFromHistory,
    cosignApproved,
  } = getIncomeAndCashRunwayData(applicationLumenResult);

  // Set details from outcomes
  incomeData.details = details;
  incomeData.cosignApproved = cosignApproved;
  incomeData.tooltipContent = buildIncomeToRentTooltip(applicationLumenResult, lumenRevision);

  // Get the formula name to check for "total" or "average"/"avg"
  const incomeToRentFormulaName = queryByAnnotation(
    applicationLumenResult,
    "income-to-rent"
  );

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    incomeData.status = outcomeStatus;
  } else {
    // Fall back to existing logic
    incomeData.status = statusFromHistory;
  }

  // Use explicit null/undefined checks so a 0 ratio (e.g. when verified
  // income is $0) still renders as "Avg Income to Rent Ratio: 0" instead of
  // collapsing to the generic "Income Issues" label (TI-2355).
  const hasIncomeToRentRatio =
    incomeToRentRatio !== null && incomeToRentRatio !== undefined;
  const hasCashRunway = cashRunway !== null && cashRunway !== undefined;

  const totalVerifiedIncomeCents = queryByAnnotation(
    applicationLumenResult,
    "total-verified-income"
  );

  const idealIncomeRentRatio = getIdealIncomeRentRatio(
    applicationLumenResult,
    lumenRevision
  );

  const groupIncomeLines = getDealGroupIncomeLines(
    totalVerifiedIncomeCents,
    incomeToRentRatio,
    idealIncomeRentRatio,
    enableMonthlyIncomeDisplay,
    monthlyRentDollars
  );

  if (hasIncomeToRentRatio) {
    const incomeToRentLabel = getNumericLabel(
      "income-to-rent",
      incomeToRentRatio,
      incomeToRentFormulaName
    );
    const labelParts = [incomeToRentLabel, ...groupIncomeLines];
    if (hasCashRunway) {
      labelParts.push(getNumericLabel("cash-runway", cashRunway, null));
    }
    incomeData.label = labelParts.join("\n");
  } else if (hasCashRunway) {
    const labelParts = [
      getNumericLabel("cash-runway", cashRunway, null),
      ...groupIncomeLines,
    ];
    incomeData.label = labelParts.join("\n");
  } else {
    const baseLabel = getBaseCategoryLabel("income", incomeData.status);
    incomeData.label =
      groupIncomeLines.length > 0
        ? [baseLabel, ...groupIncomeLines].join("\n")
        : baseLabel;
  }

  return incomeData;
}

/**
 * Process credit history data from applicant results
 */
export function processCreditHistory({
  applicationLumenResult,
  isLumenStillCalculating,
  lumenRun,
  applications,
}: {
  applicationLumenResult: any;
  isLumenStillCalculating: boolean;
  lumenRun: any;
  applications?: App[];
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  const expiredResult = getExpiredBackgroundReportsResult(applications);
  if (expiredResult) {
    return expiredResult;
  }

  // First check if background report is incomplete
  if (isBackgroundReportIncomplete(lumenRun)) {
    // Only if incomplete, get the details
    return getIncompleteBackgroundDetails(lumenRun);
  }

  const creditHistoryData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["credit"]
  );

  // Set details from outcomes
  creditHistoryData.details = details;

  // Query both average and total credit scores
  const avgCreditScoreValue = queryByAnnotation(applicationLumenResult, [
    "credit-avg-credit-score",
    "avg-credit-score",
  ]);

  const totalCreditScoreValue = queryByAnnotation(applicationLumenResult, [
    "credit-total-credit-score",
    "total-credit-score",
  ]);

  const creditScoreValue = avgCreditScoreValue ?? totalCreditScoreValue;

  const isUsingAvgScore = avgCreditScoreValue !== undefined;
  const creditScoreType = isUsingAvgScore ? "avg" : "total";

  // Get the formula name based on which credit score type we're using
  const creditScoreFormulaName = queryByAnnotation(
    applicationLumenResult,
    [
      `credit-${creditScoreType}-credit-score`,
      `${creditScoreType}-credit-score`,
    ],
    "formula"
  );

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    creditHistoryData.status = outcomeStatus;
  } else {
    // Fall back to existing logic

    const creditScoreIdealResult = queryByAnnotation(applicationLumenResult, [
      `credit-${creditScoreType}-credit-score-ideal`,
      `${creditScoreType}-credit-score-ideal`,
    ]);

    const creditScoreConditionalResult = queryByAnnotation(
      applicationLumenResult,
      [
        `credit-${creditScoreType}-credit-score-range-conditional`,
        `${creditScoreType}-credit-score-range-conditional`,
      ]
    );

    if (
      creditScoreIdealResult !== undefined ||
      creditScoreConditionalResult !== undefined
    ) {
      if (creditScoreIdealResult) {
        creditHistoryData.status = "pass";
      } else if (creditScoreConditionalResult) {
        creditHistoryData.status = "warn";
      } else {
        creditHistoryData.status = "fail";
      }
    }
  }

  // First try to get outcome-based label
  const outcomeLabel = getOutcomeBasedLabel(
    "credit",
    creditHistoryData.status,
    normalizeOutcomesToArray(lumenRun?.outcomes)
  );

  const creditScoreLabel =
    creditScoreValue !== null && typeof creditScoreValue === "number"
      ? getNumericLabel("credit", creditScoreValue, creditScoreFormulaName)
      : null;

  const labelParts = [creditScoreLabel, outcomeLabel].filter(Boolean);
  creditHistoryData.label =
    labelParts.length > 0
      ? labelParts.join("\n")
      : getBaseCategoryLabel("credit", creditHistoryData.status);

  const cosignApplicationStatus = queryByAnnotation(
    applicationLumenResult,
    "has-approved-cosign"
  );

  if (cosignApplicationStatus) {
    creditHistoryData.cosignApproved = true;
    creditHistoryData.status = "pass";
  }

  return creditHistoryData;
}

/**
 * Process rental history data from applicant results
 */
export function processRentalHistory({
  isLumenStillCalculating,
  lumenRun,
  applicationLumenResult,
  applications,
}: {
  isLumenStillCalculating: boolean;
  lumenRun: any;
  applicationLumenResult: any;
  applications?: App[];
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  const expiredResult = getExpiredBackgroundReportsResult(applications);
  if (expiredResult) {
    return expiredResult;
  }

  // First check if background report is incomplete
  if (isBackgroundReportIncomplete(lumenRun)) {
    // Only if incomplete, get the details
    return getIncompleteBackgroundDetails(lumenRun);
  }

  const rentalHistoryData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["rental"]
  );

  // Set details from outcomes
  rentalHistoryData.details = details;

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    rentalHistoryData.status = outcomeStatus;
  }

  // First try to get outcome-based label
  const outcomeLabel = getOutcomeBasedLabel(
    "rental",
    rentalHistoryData.status,
    normalizeOutcomesToArray(lumenRun?.outcomes)
  );

  // Use outcome-based label if available, otherwise use base label
  if (outcomeLabel) {
    rentalHistoryData.label = outcomeLabel;
  } else {
    rentalHistoryData.label = getBaseCategoryLabel(
      "rental",
      rentalHistoryData.status
    );
  }

  const cosignApplicationStatus = queryByAnnotation(
    applicationLumenResult,
    "has-approved-cosign"
  );
  if (cosignApplicationStatus) {
    rentalHistoryData.cosignApproved = true;
    rentalHistoryData.status = "pass";
  }

  return rentalHistoryData;
}

/**
 * Process criminal history data from applicant results
 */
export function processCriminalHistory({
  applicantLumenResults,
  isLumenStillCalculating,
  lumenRun,
  applicationLumenResult,
  applications,
}: {
  applicantLumenResults: any[];
  isLumenStillCalculating: boolean;
  lumenRun: any;
  applicationLumenResult: any;
  applications?: App[];
}): CategoryResult {
  // If lumen is still calculating, show in-progress status
  if (isLumenStillCalculating) {
    return getPendingStatus();
  }

  const expiredResult = getExpiredBackgroundReportsResult(applications);
  if (expiredResult) {
    return expiredResult;
  }

  // First check if background report is incomplete
  if (isBackgroundReportIncomplete(lumenRun)) {
    // Only if incomplete, get the details
    return getIncompleteBackgroundDetails(lumenRun);
  }

  const criminalHistoryData = createDefaultCategoryData();

  // Get status and details from outcomes
  const { status: outcomeStatus, details } = getCategoryStatusFromOutcomes(
    lumenRun,
    ["criminal"]
  );

  // Set details from outcomes
  criminalHistoryData.details = details;

  // Use outcome status if available, otherwise proceed with regular logic
  if (outcomeStatus) {
    criminalHistoryData.status = outcomeStatus;
  } else {
    // Fall back to existing logic
    const statuses = [];

    // Check each applicant's lumen result for criminal history
    applicantLumenResults.forEach(({ lumenResult }) => {
      const criminalHistoryResult = queryByAnnotation(lumenResult, [
        "criminal-history",
        "background-check",
      ]);

      if (criminalHistoryResult !== undefined) {
        statuses.push(criminalHistoryResult ? "pass" : "fail");
      }
    });

    // Determine aggregate status from all applicants
    if (statuses.length > 0) {
      criminalHistoryData.status = determineAggregateStatus(statuses);
    }
  }

  criminalHistoryData.label = getBaseCategoryLabel(
    "criminal",
    criminalHistoryData.status
  );

  const cosignApplicationStatus = queryByAnnotation(
    applicationLumenResult,
    "has-approved-cosign"
  );
  if (cosignApplicationStatus) {
    criminalHistoryData.cosignApproved = true;
    criminalHistoryData.status = "pass";
  }

  return criminalHistoryData;
}

// --------------------------
// Exports
// --------------------------

/**
 * Map of category processors by ID
 */
export const categoryProcessorMap = {
  [CategoryId.ID_VERIFICATION]: processIdVerification,
  [CategoryId.INCOME]: processIncome,
  [CategoryId.CREDIT_HISTORY]: processCreditHistory,
  [CategoryId.RENTAL_HISTORY]: processRentalHistory,
  [CategoryId.CRIMINAL_HISTORY]: processCriminalHistory,
};
